import {
  normalizeCoordinate,
  normalizeNumber,
  normalizeText,
} from './ocupacaoRedeAnalytics';

export const SINAL_FILTER_FIELDS = {
  cidade: 'cidade',
  projeto: 'projeto',
  pop: 'pop',
  classificacao_sinal: 'classificacao_sinal',
};

export const SIGNAL_CLASSIFICATION_ORDER = ['Ótimo', 'Bom', 'Ruim', 'Crítico'];
export const SIGNAL_NO_DATA_CLASSIFICATION = 'Sem dados';

export const SIGNAL_CLASSIFICATION_META = {
  Ótimo: {
    label: 'Ótimo',
    color: '#16a34a',
    chip: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300',
  },
  Bom: {
    label: 'Bom',
    color: '#2563eb',
    chip: 'border-blue-400/30 bg-blue-500/10 text-blue-600 dark:text-blue-300',
  },
  Ruim: {
    label: 'Ruim',
    color: '#f59e0b',
    chip: 'border-amber-400/30 bg-amber-500/10 text-amber-600 dark:text-amber-300',
  },
  Crítico: {
    label: 'Crítico',
    color: '#ef4444',
    chip: 'border-rose-400/30 bg-rose-500/10 text-rose-600 dark:text-rose-300',
  },
  [SIGNAL_NO_DATA_CLASSIFICATION]: {
    label: SIGNAL_NO_DATA_CLASSIFICATION,
    color: '#64748b',
    chip: 'border-slate-400/30 bg-slate-500/10 text-slate-600 dark:text-slate-300',
  },
};

const VALID_CLASSIFICATIONS = new Set(SIGNAL_CLASSIFICATION_ORDER);

const classificationKey = (value) => normalizeText(value, '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase('pt-BR');

export const normalizeSignalFilterLabel = (field, value) => {
  const normalizedValue = normalizeText(value);

  switch (field) {
    case SINAL_FILTER_FIELDS.cidade:
      return `Cidade: ${normalizedValue}`;
    case SINAL_FILTER_FIELDS.projeto:
      return `Projeto: ${normalizedValue}`;
    case SINAL_FILTER_FIELDS.pop:
      return `POP: ${normalizedValue}`;
    case SINAL_FILTER_FIELDS.classificacao_sinal:
      return `Sinal: ${normalizedValue}`;
    default:
      return normalizedValue;
  }
};

export const sameSignalFilter = (left, right) => (
  left?.field === right?.field
  && String(left?.value ?? '') === String(right?.value ?? '')
);

export const calculateSignalClassification = (mediaRx) => {
  const rx = Number(mediaRx);

  if (!Number.isFinite(rx)) {
    return null;
  }

  if (rx >= -18) {
    return 'Ótimo';
  }

  if (rx >= -22) {
    return 'Bom';
  }

  if (rx >= -25) {
    return 'Ruim';
  }

  return 'Crítico';
};

export const normalizeSignalClassification = (value) => {
  const found = SIGNAL_CLASSIFICATION_ORDER.find(
    (classification) => classificationKey(classification) === classificationKey(value),
  );

  return found || null;
};

export const hasValidSignalCoordinates = (row) => (
  Number.isFinite(row?.latitude)
  && Number.isFinite(row?.longitude)
  && !(Number(row.latitude) === 0 && Number(row.longitude) === 0)
);

export const normalizeSignalRow = (row, index = 0) => {
  const mediaRx = Number(row?.media_sinal_rx);
  const mediaTx = Number(row?.media_sinal_tx);
  const receivedClassification = normalizeSignalClassification(row?.classificacao_sinal);
  const calculatedClassification = calculateSignalClassification(mediaRx);
  const classification = receivedClassification || calculatedClassification || SIGNAL_NO_DATA_CLASSIFICATION;

  return {
    id_cto: row?.id_cto ?? `sinal-${index}`,
    caixa: normalizeText(row?.caixa),
    latitude: normalizeCoordinate(row?.latitude),
    longitude: normalizeCoordinate(row?.longitude),
    media_sinal_rx: Number.isFinite(mediaRx) ? mediaRx : null,
    media_sinal_tx: Number.isFinite(mediaTx) ? mediaTx : null,
    qtd_clientes: normalizeNumber(row?.qtd_clientes),
    classificacao_sinal: classification,
    classificacao_recebida: receivedClassification,
    classificacao_calculada: calculatedClassification,
    classificacao_divergente: Boolean(
      receivedClassification
      && calculatedClassification
      && receivedClassification !== calculatedClassification
    ),
  };
};

export const deduplicateSignalRows = (rows) => {
  const map = new Map();

  rows.forEach((row, index) => {
    const normalized = normalizeSignalRow(row, index);
    const key = String(normalized.id_cto ?? '').trim();

    if (!key) {
      return;
    }

    const current = map.get(key);
    if (!current) {
      map.set(key, normalized);
      return;
    }

    map.set(key, {
      ...current,
      ...normalized,
      classificacao_sinal: current.classificacao_recebida
        ? current.classificacao_sinal
        : normalized.classificacao_sinal,
      classificacao_recebida: current.classificacao_recebida || normalized.classificacao_recebida,
      classificacao_divergente: current.classificacao_divergente || normalized.classificacao_divergente,
    });
  });

  return Array.from(map.values());
};

export const buildOccupationIndex = (ocupacaoRows) => {
  const index = new Map();

  ocupacaoRows.forEach((row) => {
    const key = String(row?.id_cto ?? '').trim();
    if (key) {
      index.set(key, row);
    }
  });

  return index;
};

export const enrichSignalRows = (signalRows, ocupacaoRows) => {
  const occupationIndex = buildOccupationIndex(ocupacaoRows);

  return signalRows.map((row) => {
    const occupation = occupationIndex.get(String(row.id_cto ?? '').trim());

    return {
      ...row,
      cidade: occupation ? normalizeText(occupation.cidade) : '',
      id_cidade: occupation?.id_cidade ?? null,
      projeto: occupation ? normalizeText(occupation.projeto) : '',
      pop: occupation ? normalizeText(occupation.pop) : '',
      capacidade: occupation ? normalizeNumber(occupation.capacidade) : 0,
      has_ocupacao: Boolean(occupation),
    };
  });
};

export const applySignalFilters = (rows, filters) => {
  if (!Array.isArray(filters) || filters.length === 0) {
    return rows;
  }

  const groupedFilters = filters.reduce((acc, filter) => {
    if (!filter?.field) {
      return acc;
    }

    if (!acc[filter.field]) {
      acc[filter.field] = [];
    }

    acc[filter.field].push(filter.value);
    return acc;
  }, {});

  return rows.filter((row) => Object.entries(groupedFilters).every(([field, values]) => {
    const rowValue = field === SINAL_FILTER_FIELDS.classificacao_sinal
      ? normalizeSignalClassification(row?.classificacao_sinal)
      : normalizeText(row?.[field], '');

    if (!rowValue) {
      return false;
    }

    return values.some((value) => String(rowValue) === String(normalizeText(value, '')));
  }));
};

export const buildOptionsFromRows = (rows, field) => Array.from(
  new Set(rows.map((row) => normalizeText(row?.[field], '')).filter(Boolean)),
).sort((a, b) => a.localeCompare(b, 'pt-BR'));

export const buildClassificationSummary = (rows) => {
  const counters = SIGNAL_CLASSIFICATION_ORDER.reduce((acc, classification) => {
    acc[classification] = 0;
    return acc;
  }, {});
  let noDataCount = 0;

  rows.forEach((row) => {
    const classification = normalizeSignalClassification(row?.classificacao_sinal);
    if (VALID_CLASSIFICATIONS.has(classification)) {
      counters[classification] += 1;
      return;
    }

    if (row?.classificacao_sinal === SIGNAL_NO_DATA_CLASSIFICATION) {
      noDataCount += 1;
    }
  });

  const total = Object.values(counters).reduce((sum, count) => sum + count, 0);

  return {
    items: SIGNAL_CLASSIFICATION_ORDER.map((classification) => ({
      key: classification,
      label: classification,
      count: counters[classification],
      percent: total > 0 ? (counters[classification] / total) * 100 : 0,
      color: SIGNAL_CLASSIFICATION_META[classification].color,
    })),
    noDataCount,
  };
};

export const buildAverageRxByDimension = (rows, dimension, idDimension = null) => {
  const map = new Map();

  rows.forEach((row) => {
    const label = normalizeText(row?.[dimension], '');
    const rx = Number(row?.media_sinal_rx);

    if (!label || !Number.isFinite(rx)) {
      return;
    }

    const key = idDimension && row?.[idDimension] !== null && row?.[idDimension] !== undefined
      ? String(row[idDimension])
      : label;

    const current = map.get(key) || {
      key,
      label,
      total: 0,
      count: 0,
    };

    current.total += rx;
    current.count += 1;
    map.set(key, current);
  });

  return Array.from(map.values())
    .map((item) => ({
      ...item,
      media_rx: item.count > 0 ? item.total / item.count : 0,
    }))
    .sort((left, right) => left.media_rx - right.media_rx || left.label.localeCompare(right.label, 'pt-BR'));
};

export const calculateCableAmount = (qtdCabo, activeProjects = []) => {
  const total = Number(qtdCabo?.quantidade_cabo);
  const safeTotal = Number.isFinite(total) ? total : 0;
  const byProject = Array.isArray(qtdCabo?.por_projeto) ? qtdCabo.por_projeto : [];

  if (activeProjects.length === 0 || byProject.length === 0) {
    return {
      value: safeTotal,
      scopedByProject: false,
    };
  }

  const activeSet = new Set(activeProjects.map(String));
  const scopedValue = byProject.reduce((acc, row) => (
    activeSet.has(String(row?.projeto ?? ''))
      ? acc + normalizeNumber(row?.quantidade_cabo)
      : acc
  ), 0);

  return {
    value: scopedValue,
    scopedByProject: true,
  };
};

export const buildSignalDashboardSnapshot = (rows, qtdCabo, activeProjects = []) => {
  const { items: classificationSummary, noDataCount } = buildClassificationSummary(rows);
  const cable = calculateCableAmount(qtdCabo, activeProjects);
  const divergenceCount = rows.filter((row) => row.classificacao_divergente).length;
  const validRxRows = rows.filter((row) => Number.isFinite(Number(row.media_sinal_rx)));
  const validTxRows = rows.filter((row) => Number.isFinite(Number(row.media_sinal_tx)));

  return {
    summary: {
      quantidadeCtos: rows.length,
      quantidadeClientes: rows.reduce((acc, row) => acc + normalizeNumber(row?.qtd_clientes), 0),
      mediaRxGeral: validRxRows.length > 0
        ? validRxRows.reduce((acc, row) => acc + Number(row.media_sinal_rx), 0) / validRxRows.length
        : 0,
      mediaTxGeral: validTxRows.length > 0
        ? validTxRows.reduce((acc, row) => acc + Number(row.media_sinal_tx), 0) / validTxRows.length
        : 0,
      divergenciasClassificacao: divergenceCount,
      quantidadeCabo: cable.value,
      caboFiltradoPorProjeto: cable.scopedByProject,
    },
    classificationSummary,
    noDataClassificationCount: noDataCount,
    averages: {
      pop: buildAverageRxByDimension(rows, 'pop'),
      cidade: buildAverageRxByDimension(rows, 'cidade', 'id_cidade'),
    },
  };
};
